<template>
  <div
    class="review-card-meta"
    :class="isMobile ? 'd-flex flex-column' : 'review-card-meta--aside'"
  >
    <div
      class="d-flex align-center"
      :class="{ 'justify-space-between': isMobile }"
    >
      <div>
        <meta
          itemprop="itemReviewed"
          :content="itemReviewed"
        >
        <div
          class="review-card-meta__author text-subtitle-1 primary--text"
          :class="{ 'mb-1': !isMobile }"
          itemprop="author"
          v-bind="authorAttrs"
        >
          <div
            v-if="author.patientProfileId"
            class="review-card-meta__author-link"
            data-qa="patient_profile__node_author_link"
            :data-patient-data="patientData"
            @click.stop="onAuthorClick"
          >
            {{ author.label }}
          </div>
          <template v-else>
            {{ author.label }}
          </template>
        </div>
      </div>
      <ReviewCardLogo v-if="isMobile" />
    </div>

    <div
      v-if="participationLabel"
      class="text-body-2 text-secondary--text"
      :class="{ 'mb-1': !isMobile }"
    >
      {{ participationLabel }}
    </div>

    <div
      class="text-body-2 text-secondary--text"
      :class="{ 'mb-5': !isMobile }"
      itemprop="datePublished"
      :content="isoDate"
    >
      {{ publishedDate }}
    </div>

    <div :class="{ 'mt-3': isMobile }">
      <ReviewCardTooltips :rate="rate" />
    </div>

    <div
      v-if="avgRate != null"
      itemprop="reviewRating"
      itemscope
      itemtype="https://schema.org/Rating"
    >
      <meta
        itemprop="ratingValue"
        :content="ratingValue"
      >
      <meta
        itemprop="worstRating"
        content="0"
      >
      <meta
        itemprop="bestRating"
        content="100"
      >
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import useIsMobile from 'src/composables/useIsMobile'
import filterRateMarkPercentFilter from 'common/filters/filterRateMarkPercentFilter'
import filterPhoneNumberAnonymize from 'common/filters/filterPhoneNumberAnonymize'
import tDynamic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tDynamic'
import tStatic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tStatic'
import type { CommonContextStore } from 'src/types/context'
import useContextStore from 'src/store/useContextStore'
import type { Lpu } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_types'
import type { Rate } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/types'
import type { PatientProfileOpenPayload } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/components/PatientProfile/types'
import ReviewCardLogo from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/components/ReviewCardLogo.vue'
import ReviewCardTooltips from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/components/ReviewCardTooltips.vue'
import getAuthorInfo from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/functions/getAuthorInfo'
import {
  formatPublishedDate,
  formatPublishedIsoDate,
} from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/functions/formatReviewDates'
import { getAvgRate } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/functions/getRateMeta'

const PARTICIPATION_LABELS: Record<string, string> = {
  with_child: 'Отзыв родителя',
  accompanying_adult: 'Отзыв сопровождающего',
  remote_reviewer: 'Отзыв родственника со слов пациента',
}

const props = defineProps<{
  rate: Rate
  lpu: Lpu
}>()

const isMobile = useIsMobile()

const emit = defineEmits(['open-author'])

const { context }: CommonContextStore = useContextStore()
const author = computed(() => getAuthorInfo(props.rate))
const authorAttrs = computed(() => (author.value.isPrivate ? { data: 'private' } : {}))
const patientData = computed(() => {
  if (!author.value.patientProfileId) {
    return undefined
  }

  return JSON.stringify({
    patientTitle: author.value.patientTitle
      || (props.rate.patient_profile?.phone
        ? filterPhoneNumberAnonymize(props.rate.patient_profile.phone)
        : ''),
    patientProfileId: author.value.patientProfileId,
  })
})
const participationLabel = computed(() => {
  const key = props.rate.participation

  return key && PARTICIPATION_LABELS[key]
    ? tStatic(PARTICIPATION_LABELS[key])
    : ''
})
const publishedDate = computed(() => formatPublishedDate(
  props.rate.dt_created,
  context.current_language.iso,
))
const isoDate = computed(() => formatPublishedIsoDate(props.rate.dt_created))
const avgRate = computed(() => getAvgRate(props.rate))
const ratingValue = computed(() => (
  avgRate.value == null ? '' : String(filterRateMarkPercentFilter(avgRate.value))
))
const itemReviewed = computed(() => (
  tDynamic(props.rate.doctor, 'fio') || tDynamic(props.lpu, 'name')
))

function onAuthorClick() {
  if (!author.value.patientProfileId) {
    return
  }

  const payload: PatientProfileOpenPayload = {
    patientTitle: author.value.patientTitle || '',
    patientProfileId: author.value.patientProfileId,
  }

  emit('open-author', payload)
}
</script>

<style scoped lang="scss">
.review-card-meta {
  &--aside {
    width: 216px;
    flex-shrink: 0;
  }

  &__author {
    white-space: nowrap;
  }

  &__author-link {
    display: inline;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }
}
</style>
